// Standard atomic weights for molar mass calculation
export const atomicWeights: Record<string, number> = {
  H: 1.008, He: 4.003, Li: 6.941, Be: 9.012, B: 10.81, C: 12.011,
  N: 14.007, O: 15.999, F: 18.998, Ne: 20.18, Na: 22.99, Mg: 24.305,
  Al: 26.982, Si: 28.086, P: 30.974, S: 32.06, Cl: 35.45, Ar: 39.948,
  K: 39.098, Ca: 40.078, Sc: 44.956, Ti: 47.867, V: 50.942, Cr: 51.996,
  Mn: 54.938, Fe: 55.845, Co: 58.933, Ni: 58.693, Cu: 63.546, Zn: 65.38,
  Ga: 69.723, Ge: 72.63, As: 74.922, Se: 78.971, Br: 79.904, Kr: 83.798,
  Rb: 85.468, Sr: 87.62, Y: 88.906, Zr: 91.224, Nb: 92.906, Mo: 95.95,
  Ru: 101.07, Rh: 102.906, Pd: 106.42, Ag: 107.868, Cd: 112.414, In: 114.818,
  Sn: 118.71, Sb: 121.76, Te: 127.6, I: 126.904, Xe: 131.293, Cs: 132.905,
  Ba: 137.327, La: 138.905, Ce: 140.116, Pr: 140.908, Nd: 144.242, Sm: 150.36,
  Eu: 151.964, Gd: 157.25, Tb: 158.925, Dy: 162.5, Ho: 164.93, Er: 167.259,
  Tm: 168.934, Yb: 173.045, Lu: 174.967, Hf: 178.49, Ta: 180.948, W: 183.84,
  Re: 186.207, Os: 190.23, Ir: 192.217, Pt: 195.084, Au: 196.967, Hg: 200.592,
  Tl: 204.383, Pb: 207.2, Bi: 208.98, Th: 232.038, Pa: 231.036, U: 238.029,
};

export interface ElementCount {
  symbol: string;
  count: number;
  mass: number;
  totalMass: number;
  percentage: number;
}

interface ParseResult {
  counts: Record<string, number>;
  error?: string;
}

function parseGroup(formula: string, index: number): { counts: Record<string, number>; newIndex: number } {
  const counts: Record<string, number> = {};

  while (index < formula.length) {
    if (formula[index] === "(") {
      const sub = parseGroup(formula, index + 1);
      index = sub.newIndex;
      // Read multiplier after closing paren
      let numStr = "";
      while (index < formula.length && /\d/.test(formula[index])) {
        numStr += formula[index];
        index++;
      }
      const mult = numStr ? parseInt(numStr) : 1;
      for (const [el, c] of Object.entries(sub.counts)) {
        counts[el] = (counts[el] || 0) + c * mult;
      }
    } else if (formula[index] === ")") {
      index++;
      return { counts, newIndex: index };
    } else if (/[A-Z]/.test(formula[index])) {
      let symbol = formula[index];
      index++;
      while (index < formula.length && /[a-z]/.test(formula[index])) {
        symbol += formula[index];
        index++;
      }
      let numStr = "";
      while (index < formula.length && /\d/.test(formula[index])) {
        numStr += formula[index];
        index++;
      }
      const count = numStr ? parseInt(numStr) : 1;
      counts[symbol] = (counts[symbol] || 0) + count;
    } else {
      index++;
    }
  }

  return { counts, newIndex: index };
}

export function parseFormula(formula: string): ParseResult {
  // Strip spaces and common decorative chars
  const cleaned = formula.replace(/\s+/g, "").replace(/·/g, "").replace(/•/g, "");
  if (!cleaned) return { counts: {}, error: "Empty formula" };

  const { counts } = parseGroup(cleaned, 0);

  // Validate all symbols
  for (const sym of Object.keys(counts)) {
    if (!(sym in atomicWeights)) {
      return { counts: {}, error: `Unknown element: ${sym}` };
    }
  }

  return { counts };
}

export function calculateMolarMass(formula: string): { total: number; breakdown: ElementCount[]; error?: string } {
  const { counts, error } = parseFormula(formula);
  if (error) return { total: 0, breakdown: [], error };

  let total = 0;
  const breakdown: ElementCount[] = [];

  for (const [symbol, count] of Object.entries(counts)) {
    const mass = atomicWeights[symbol];
    const totalMass = mass * count;
    total += totalMass;
    breakdown.push({ symbol, count, mass, totalMass, percentage: 0 });
  }

  // Calculate percentages
  for (const item of breakdown) {
    item.percentage = total > 0 ? (item.totalMass / total) * 100 : 0;
  }

  // Sort by count descending
  breakdown.sort((a, b) => b.totalMass - a.totalMass);

  return { total, breakdown };
}
